import {
    ACESFilmicToneMapping,
    AnimationMixer,
    Clock,
    Fog,
    Scene,
    sRGBEncoding,
    WebGLRenderer,
    BoxBufferGeometry,
    Mesh,
    MeshBasicMaterial,
    Color,
    Raycaster,
    Vector3,
    TextureLoader,
    PlaneBufferGeometry,
    Group,
    MeshStandardMaterial,
    PerspectiveCamera,
    Box3,
    LoadingManager,
    PCFSoftShadowMap, VSMShadowMap, MeshPhongMaterial, InstancedMesh, MeshPhysicalMaterial,
} from "three";
import {GLTFLoader} from "./vendor/GLTFLoader";
import {DRACOLoader} from "./vendor/DRACOLoader";
import LightsGroup from "./mods/LightsGroup";
import {OrbitControls} from "./vendor/OrbitControls";
import {animate} from "popmotion/dist/popmotion.min";
import Stats from 'stats.js'
import PointerManager from "./mods/PointerManager";
import POI from "./mods/POI";
import SelectionMaterial from "./mods/SelectionMaterial";
import {lerp} from "./utils/lerp";
import clamp from "./utils/clamp";
import {markerObjectDimensions, offsetBetweenMarkerAndText, textObjectHeight} from "./const/metrics";
import {KTX2Loader} from "./vendor/KTX2Loader";
import {POIs} from "./const/POIToMeshName";
import GLTFMeshGpuInstancingExtension from "./vendor/GLTFInstance";

const aspect = window.innerWidth / window.innerHeight

// Main class
export default class GLApp {
    // camera
    // camera = new OrthographicCamera(-aspect * frustumSize, aspect * frustumSize, frustumSize, -frustumSize, 0, 1000);
    camera = new PerspectiveCamera(12, aspect, 0.1, 3000)
    // three.js webgl renderer
    renderer = new WebGLRenderer({ antialias: true, logarithmicDepthBuffer: false, powerPreference: "high-performance" });
    // scene object
    scene = new Scene();
    // scene lights
    lights = new LightsGroup();
    // camera orbit controls
    orbit = new OrbitControls(this.camera, this.renderer.domElement);
    // clock
    clock = new Clock(false);
    // FPS stats
    stats = new Stats();
    // raycaster
    raycaster = new Raycaster();
    // pointer manager
    pointerManager = new PointerManager();
    // mixer for animations
    mixer = false;

    isMoved = false;

    get isMobile(){
        return window.innerWidth < 500;
    }

    cubeByRaycastCube = new Map();
    raycastCubeByCube = new Map();

    // points of interests managing
    selectionManage = new POI(this);

    objectsLookAtCamera = [];
    markerObjects = [];
    allCubes = [];
    groups = [];
    lastloop = Date.now();

    groupByCube = new Map();

    composer = false;

    loadingManager = new LoadingManager();

    poiDotTexture = false;

    gltfScene = false;

    gltfDynamicScene = false;

    textTextures = [];

    robotLights = [];

    resizeMesh = false;

    callbackFn = (type, data) => {};

    onZoomOut = () => {}

    onZoomIn = () => {}

    onLoadingProgress = (total) => {}

    fpsMedian = 60;

    onResize() {
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.camera.aspect = window.innerWidth / window.innerHeight;

        const boundingBox = new Box3();
        boundingBox.setFromObject( this.resizeMesh );

        const middle = new Vector3();
        const size = new Vector3();
        boundingBox.getSize(size);

        const fov = this.camera.fov * ( Math.PI / 180 );
        const fovh = 2*Math.atan(Math.tan(fov/2) * this.camera.aspect);
        let dx = size.z / 2 + Math.abs( size.x / 2 / Math.tan( fovh / 2 ) );
        let dy = size.z / 2 + Math.abs( size.y / 2 / Math.tan( fov / 2 ) );
        let cameraZ = Math.max(dx, dy);

        const cameraProportion = (cameraZ / this.camera.position.distanceTo(this.resizeMesh.position));

        let zoom = cameraProportion * (this.isMobile ? 0.8 : 1);

        glapp.camera.setViewOffset(window.innerWidth / zoom, window.innerHeight / zoom, -(window.innerWidth - (window.innerWidth / zoom)) / 2, (-(window.innerHeight - (window.innerHeight / zoom)) / 2) + 50, window.innerWidth, window.innerHeight)


        this.camera.updateProjectionMatrix();
    }

    constructor(container) {
        window.glapp = this;

        window.animate = animate;

        this.stats.showPanel(0);

        document.body.appendChild(this.stats.dom);

        window.addEventListener('resize', this.onResize.bind(this));

        const pixelRatio = Math.min(1.6, window.devicePixelRatio);

        this.renderer.setSize(window.innerWidth, window.innerHeight)
        this.renderer.setPixelRatio(pixelRatio);

        this.container = container;

        container.appendChild(this.renderer.domElement)

        const { renderer, scene, orbit, camera, lights } = this;

        renderer.domElement.style.opacity = 0;
        renderer.domElement.style.transition = 'opacity 1s cubic-bezier(0,0,0,1)';
        //
        if (!window.location.href.includes('shadows')) {
            renderer.shadowMap.enabled = true;
            renderer.shadowMap.type = PCFSoftShadowMap;
            // renderer.shadowMap.renderReverseSided = true;
        }

        // this.scene.scale.set(1,1,1)

        // add blue sky fog
        if (!window.location.href.includes('fog')) {
            scene.fog = new Fog( new Color(0.7, 0.9, 0.9), 22, 26);
            // scene.fog = new FogExp2(new Color(0.7, 0.9, 0.9), 0.01);
        }

        scene.background = new Color(0.7, 0.9, 0.9);

        // config camera orbit
        orbit.enableRotate = true;
        orbit.enableDamping = true; // an animation loop is required when either damping or auto-rotation are enabled
        orbit.dampingFactor = 0.05;
        orbit.screenSpacePanning = false;
        orbit.maxPolarAngle = 1.16
        orbit.minPolarAngle = 0.4
        orbit.minDistance = 20;
        orbit.maxDistance = 20;
        orbit.enableZoom = true;
        orbit.zoomSpeed = 0.4;
        // orbit.maxZoom = 0.1;

        // setup renderer

        // renderer.setPixelRatio( window.devicePixelRatio );
        // renderer.setSize( window.innerWidth, window.innerHeight );
        renderer.toneMapping = ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1;
        renderer.outputEncoding = sRGBEncoding;


        // default camera position and rotation
        camera.position.set(47.239963661067854, 39.93395294062732, 79.1181082820843);
        camera.rotation.set(-0.5344718509492761, 0.5056708350132413, 0.27924065623264616);
        // if (this.isMobile) {
        //     camera.position.set(8.125636218502958, 17.941975501057115, 3.4726864476971193);
        //     camera.rotation.set(-1.3796093944424441, 0.41838119985482347, 1.126209750755947);
        // } else {
        //     camera.position.set(47.239963661067854, 39.93395294062732, 79.1181082820843);
        //     camera.rotation.set(-0.5344718509492761, 0.5056708350132413, 0.27924065623264616);
        // }

        this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
            this.onLoadingProgress(itemsLoaded/itemsTotal)
            if (itemsLoaded === itemsTotal) {
                this.whenReady();
            }
        };

        // setup draco loader
        const dracoLoader = new DRACOLoader(this.loadingManager);

        dracoLoader.setDecoderPath( '/draco/' );
        dracoLoader.preload();

        const ktx2Loader = new KTX2Loader(this.loadingManager);
        ktx2Loader.setTranscoderPath('/basis/')
        ktx2Loader.detectSupport(this.renderer)

        // setup gltf loader and attach draco to it
        const loader = new GLTFLoader(this.loadingManager).setPath( '/models/gltf/' );
        loader.setDRACOLoader(dracoLoader)
        loader.setKTX2Loader(ktx2Loader);
        loader.register(parser => new GLTFMeshGpuInstancingExtension(parser))

        const texLoader = new TextureLoader(this.loadingManager).setPath('/imgs/')

        texLoader.load('poi_dot.png', poiDotTexture => (this.poiDotTexture = poiDotTexture))

        loader.load('scene_dynamic.glb', gltf => (this.gltfDynamicScene = gltf));

        loader.load('scene.glb', gltf => (this.gltfScene = gltf));

        POIs.forEach(poiName => {
            texLoader.load(poiName + '.png', (text) => (this.textTextures[poiName] = text))
        })

        // add lights to scene
        scene.add(lights);

        // offset scene a bit
        scene.position.set(0.8, 0, 0.5)

        // start tick
        window.requestAnimationFrame(() => this.update());
    }

    whenReady () {
        this.scene.add(this.gltfDynamicScene.scene);
        this.scene.add(this.gltfScene.scene);

        // define mixer for animations
        this.mixer = new AnimationMixer(this.gltfDynamicScene.scene);

        // attach anim
        this.gltfDynamicScene.animations.forEach((clip) => {
            this.mixer.clipAction(clip).play();
        });

        // shadows for specific objects
        this.scene.traverse((node, i) => {

            if (node.isMesh && node.material instanceof MeshPhysicalMaterial) {
            //     // console.log(node, 'physical')
                node.removeFromParent();
            //     node.material = new MeshStandardMaterial({
            //         // map: node.material.map,
            //         transparent: true,
            //         opacity: node.material.opacity * 2,
            //         color: node.material.color,
            //         name: node.material.name,
            //         side: 2,
            //     });
             }

            if (node.isGroup && node.name.indexOf('robot') === 0) {
                node.children[3].material.transparent = true;
                this.robotLights.push(node.children[3]);

            }

            if (node.isMesh && node.name.indexOf("Cube_") === 0 && node.name.indexOf('small') === -1) {
                const raycastCube = this.gltfScene.scene.getObjectByName(`${node.name}_small`);
                this.cubeByRaycastCube.set(raycastCube, node);
                this.raycastCubeByCube.set(node, raycastCube);

                node.material = new SelectionMaterial();
                this.allCubes.push(node);
                this.selectionManage.selectableObjects.push(raycastCube);
                this.selectionManage.selectionMaterials.push(node.material);

                raycastCube.visible = false;
            }

            // shadows
            if (node.isMesh) {
                node.receiveShadow = true;
                if (
                    node.name.indexOf('CITY_WIND_CATCHER') === 0 ||
                    node.name.indexOf('wind_turbine') === 0 ||
                    node.name.indexOf('Car') === 0 ||
                    node.name.indexOf('Bus') === 0 ||
                    node.name.indexOf('robot') === 0 ||
                    node.name.indexOf('baloon') === 0
                ) {
                    // this.csm.setupMaterial(node.material);
                    if (!window.location.href.includes('shadows')) {
                        node.castShadow = true;
                        node.receiveShadow = false;
                    }
                }
            } else if (node.isGroup) {
                if (
                    node.name.indexOf('CITY_WIND_CATCHER') === 0 ||
                    node.name.indexOf('wind_turbine') === 0 ||
                    node.name.indexOf('Car') === 0 ||
                    node.name.indexOf('Bus') === 0 ||
                    node.name.indexOf('robot') === 0
                ) {
                    node.children.forEach(node => {
                        if (node.isMesh) {
                            // this.csm.setupMaterial(node.material);
                            if (!window.location.href.includes('shadows')) {
                                node.castShadow = true;
                                node.receiveShadow = false;
                            }
                        }
                    })
                }
            }

        } );
            //
            //
        setTimeout(() => {
            // marker geometry buffer
            const markerGeometry = new PlaneBufferGeometry(markerObjectDimensions, markerObjectDimensions,1,1);

            this.selectionManage.selectableObjects.forEach(node_ => {
                const group = new Group();
                const node = this.cubeByRaycastCube.get(node_);
                const vec = new Vector3();

                const textTexture = this.textTextures[node.name];

                // text plane material
                const mat = new MeshStandardMaterial({
                    map: textTexture,
                    transparent: true,
                    opacity: 0,
                    color: new Color(1,1,1),
                    emissiveIntensity: 1,
                    emissive: new Color(1,1,1)
                });
                // text width
                const textObjectWidth = textObjectHeight * (textTexture.image.naturalWidth / textTexture.image.naturalHeight);
                const textPlaneGeometry = new PlaneBufferGeometry(textObjectWidth, textObjectHeight,1,1);
                const textPlane = new Mesh(textPlaneGeometry, mat);
                textPlane.position.setX((textObjectWidth / 2) + (markerObjectDimensions / 2) + offsetBetweenMarkerAndText);

                // draw always on top
                textPlane.renderOrder = 999;
                textPlane.material.depthTest = false;
                textPlane.material.depthWrite = false;
                // textPlane.onBeforeRender = function (renderer) { renderer.clearDepth(); };

                const markerMaterial = new MeshStandardMaterial({
                    map: this.poiDotTexture,
                    transparent: true,
                    color: new Color(1,1,1),
                    emissiveIntensity: 1,
                    emissive: new Color(1,1,1)

                });

                const markerPlane = new Mesh(markerGeometry, markerMaterial);

                // draw always on top
                markerPlane.renderOrder = 999;
                markerPlane.material.depthTest = false;
                markerPlane.material.depthWrite = false;
                // markerPlane.onBeforeRender = function (renderer) { renderer.clearDepth(); };

                // detect POI center and set group position
                node.geometry.computeBoundingBox();
                node.geometry.boundingBox.getCenter(vec);
                node.localToWorld(vec);
                group.position.set(vec.x - 0.8, vec.y - 0.1, vec.z - 0.5);

                this.objectsLookAtCamera.push(group);

                textPlane.isText = true;
                this.markerObjects.push(markerPlane);

                group.add(textPlane, markerPlane);
                this.groups.push(group);
                this.scene.add(group);

                this.groupByCube.set(node, group);
            })
        }, 64);
        //

        setTimeout(() => {
            if (this.fpsMedian < 55) {
                renderer.shadowMap.enabled = true;
            }
        }, 15000);

        this.resizeMesh = new Mesh(new BoxBufferGeometry(6,1,4), false)
        this.scene.add(this.resizeMesh);

        this.onResize();
        this.renderer.domElement.style.opacity = 1;
        this.clock.start();

        animate({
            from: 0,
            to: 1,
            duration: 1000,
            onUpdate: (p) => {
                if (!this.selectionManage.selectedObject) {
                    this.camera.zoom = 1 - 0.1 * (p - 1);
                    this.camera.updateProjectionMatrix()
                }
            }
        })
    }

    next() {
        const currentIndex = POIs.indexOf(this.selectionManage.selectedObject.name);
        const nextIndex = currentIndex + 1;
        if (nextIndex > POIs.length - 1) {
            this.callbackFn('poi_next', {
                currentName: this.selectionManage.selectedObject.name,
                nextName: POIs[0]
            });
            this.selectionManage.selectObject(POIs[0]);
        } else {
            this.callbackFn('poi_next', {
                currentName: this.selectionManage.selectedObject.name,
                nextName: POIs[nextIndex]
            });
            this.selectionManage.selectObject(POIs[nextIndex]);
        }
    }

    prev() {
        const currentIndex = POIs.indexOf(this.selectionManage.selectedObject.name);
        const nextIndex = currentIndex - 1;
        if (nextIndex < 0) {
            this.callbackFn('poi_prev', {
                currentName: this.selectionManage.selectedObject.name,
                nextName: POIs[POIs.length - 1]
            });
            this.selectionManage.selectObject(POIs[POIs.length - 1]);
        } else {
            this.callbackFn('poi_prev', {
                currentName: this.selectionManage.selectedObject.name,
                nextName: POIs[nextIndex]
            });
            this.selectionManage.selectObject(POIs[nextIndex]);
        }
    }

    zoomOut () {
        this.onZoomOut();
        this.callbackFn('poi_deselect', {
            name: this.selectionManage.selectedObject.name
        });

        this.selectionManage.selectedObject = false;
        animate({
            from: Object.assign(this.orbit.target, { t: this.camera.zoom }),
            duration: 500,
            to: { x: 0, y: 0, z: 0, t: 1 },
            onUpdate: (t) => {
                this.orbit.target.set(t.x, t.y, t.z);
                this.camera.zoom = t.t;
                this.camera.updateProjectionMatrix();
            }
        })
    }

    update() {
        const thisloop = Date.now();
        const fps = 1000 / (thisloop - this.lastloop);
        this.lastloop = thisloop;
        const fpsCompensation = 1 / (fps / 120);
        // update FPS stats
        this.stats.begin();

        this.fpsMedian = (this.fpsMedian + fps) / 2;

        const { mixer, clock } = this;

        this.robotLights.forEach(light => {
            if (Math.floor(((this.clock.elapsedTime + (Math.random() * 10000)) * 100)) % 100 === 0) {
                light.material.color.r = 1;
            }

            light.material.color.r = clamp(light.material.color.r - 0.013 * fpsCompensation, 0, 1);
        })

        // marker and text always look at camera
        this.objectsLookAtCamera.forEach((obj) => {
            obj.lookAt(this.camera.position.x, this.camera.position.y, this.camera.position.z);
        })

        // text / marker opacity
        this.allCubes.forEach(cube => {
            const group = this.groupByCube.get(cube);
            if (!group) return;
            group.children[0].material.opacity = this.isMobile ? 0.0 : clamp(
                lerp(
                    group.children[0].material.opacity,
                    this.selectionManage.selectedObject ? 0.0 : (group.isHovered ? 1.0 : 0.0),
                    0.10 * fpsCompensation
                ),
                0.0,
                1.0
            );

            if (group.children[0].material.opacity > 0) {
                group.children[0].visible = true;
            } else {
                group.children[0].visible = false;
            }

            if (this.isMobile) {
                group.children[1].scale.set(3,3,3);
            } else {
                group.children[1].scale.set(1,1,1);
            }

            group.children[1].material.opacity = clamp(
                lerp(
                    group.children[1].material.opacity,
                    this.selectionManage.selectedObject ? 0.0 : 1.0,
                    0.09 * fpsCompensation
                ),
                0.0,
                1.0
            );

            if (group.children[1].material.opacity > 0) {
                group.children[1].visible = true;
            } else {
                group.children[1].visible = false;
            }

            cube.material.uniforms.hoverIntensity.value = clamp(
                lerp(
                    cube.material.uniforms.hoverIntensity.value,
                    this.selectionManage.selectedObject ? 0.0 : (group.isHovered ? 1.0 : 0.0),
                    this.selectionManage.selectedObject ? 0.03 * fpsCompensation : 0.08 * fpsCompensation
                ),
                0.0,
                1.0
            );
        })

        // update camera controls
        this.orbit.update();
        // clock delta
        const delta = clock.getDelta();
        // update animations
        if (mixer) mixer.update(delta);
        // render scene
        // this.composer.render();
        this.renderer.render(this.scene, this.camera)
        //
        this.stats.end();
        // loop
        window.requestAnimationFrame(() => this.update());

    }
}